import * as Sentry from "@sentry/react";
import FontObserver from "fontfaceobserver";
import { lazy, Suspense } from "react";
import { createRoot, Root } from "react-dom/client";
import { WithReactQuery } from "./app/providers/react-query/WithReactQuery.js";
import { WithTheme } from "./app/providers/waapui/WithTheme.js";
import { FullScreenLoadingComponent, FullScreenLoadingPage, LoadingPageProvider } from "./customHooks/useLoadingPage/index.js";
import { initSentry } from "./services/sentry/initSentry.js";
import "./styles/fonts.css";

initSentry();

const MOUNT_NODE = document.getElementById("root") as HTMLElement;

export const appStartTransaction = Sentry.startInactiveSpan({
    op: "app.start",
    name: "App start",
});

export const App = lazy(() => import("./app.js").then((module) => ({ default: module.App })));

// Create root only once to avoid HMR issues like creating multiple Mystique
declare global {
    interface Window {
        reactRoot?: Root;
    }
}

let root = window.reactRoot;
if (!root) {
    root = createRoot(MOUNT_NODE);
    window.reactRoot = root;
}

const render = (): void => {
    root.render(
        <WithTheme>
            <WithReactQuery>
                <LoadingPageProvider>
                    <Suspense fallback={<FullScreenLoadingPage extra={{ from: "index" }} />}>
                        <App />
                    </Suspense>

                    <FullScreenLoadingComponent />
                </LoadingPageProvider>
            </WithReactQuery>
        </WithTheme>,
    );
};

const bodyFont400 = new FontObserver("Helvetica Neue", { weight: 400 });
const bodyFont500 = new FontObserver("Helvetica Neue", { weight: 500 });
const bodyFont700 = new FontObserver("Helvetica Neue", { weight: 700 });
const bodyFont900 = new FontObserver("Helvetica Neue", { weight: 800 });

Promise.allSettled([bodyFont400.load(), bodyFont500.load(), bodyFont700.load(), bodyFont900.load()]).finally(render);
